import React, { useState } from 'react'
import {
  Form,
  Input,
  Button,
  Typography,
  Avatar,
  Divider,
  Space,
  Row,
  Col,
  message,
  Tabs,
  Descriptions,
  Tag
} from 'antd'
import type { TabsProps } from 'antd'
import { AppCard } from '@/shared/components/ui'
import {
  UserOutlined,
  MailOutlined,
  LockOutlined,
  SafetyOutlined,
  EditOutlined,
  SaveOutlined
} from '@ant-design/icons'
import { useAuthStore } from '../../stores/authStore'
import { getRoleDisplayName, getRoleColor } from '@/shared/utils/roleUtils'
import type { UserUpdateRequest, PasswordChangeRequest } from '../../types/auth.types'
import { useTranslation } from 'react-i18next'

const { Title, Text } = Typography

const ProfilePage: React.FC = () => {
  const { user, company, updateUser, changePassword } = useAuthStore()
  const { t } = useTranslation(['pages', 'common'])
  const [editMode, setEditMode] = useState(false)
  const [changePasswordMode, setChangePasswordMode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  // 个人信息表单初始值
  const initialValues = {
    username: user?.username || '',
    email: user?.email || '',
  }

  // 处理个人信息更新
  const handleProfileUpdate = async (values: any) => {
    setLoading(true)
    try {
      if (!user) return

      const updateData: UserUpdateRequest = {
        username: values.username,
        email: values.email,
      }

      await updateUser(user.id, updateData)
      message.success(t('profile.messages.updateSuccess'))
      setEditMode(false)
    } catch (error) {
      message.error(t('profile.messages.updateFailed'))
    } finally {
      setLoading(false)
    }
  }

  // 处理密码修改
  const handlePasswordChange = async (values: any) => {
    setLoading(true)
    try {
      if (!user) return

      const passwordData: PasswordChangeRequest = {
        old_password: values.currentPassword,
        new_password: values.newPassword,
      }

      await changePassword(user.id, passwordData)
      message.success(t('profile.messages.passwordChangeSuccess'))
      setChangePasswordMode(false)
      form.resetFields(['currentPassword', 'newPassword', 'confirmPassword'])
    } catch (error) {
      message.error(t('profile.messages.passwordChangeFailed'))
    } finally {
      setLoading(false)
    }
  }

  // 验证新密码
  const validatePassword = (_: any, value: string) => {
    if (!value) {
      return Promise.reject(new Error(t('profile.validation.passwordRequired')))
    }
    if (value.length < 8) {
      return Promise.reject(new Error(t('profile.validation.passwordMinLength')))
    }
    return Promise.resolve()
  }

  // 验证确认密码
  const validateConfirmPassword = ({ getFieldValue }: any) => ({
    validator(_: any, value: string) {
      if (!value || getFieldValue('newPassword') === value) {
        return Promise.resolve()
      }
      return Promise.reject(new Error(t('profile.validation.passwordMismatch')))
    },
  })

  const baseItems: TabsProps['items'] = [
    {
      key: 'profile',
      label: t('profile.tabs.profile'),
      children: (
        <AppCard>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
            <Avatar size={64} icon={<UserOutlined />} style={{ marginRight: '16px' }}>
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <Title level={4} style={{ marginBottom: '4px' }}>
                {user?.username}
              </Title>
              <Text type="secondary">{user?.email || t('profile.fields.emailNotSet')}</Text>
            </div>
            {!editMode && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setEditMode(true)}
                style={{ marginLeft: 'auto' }}
              >
                {t('profile.buttons.editProfile')}
              </Button>
            )}
          </div>

          <Divider />

          {editMode ? (
            <Form
              form={form}
              initialValues={initialValues}
              onFinish={handleProfileUpdate}
              layout="vertical"
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="username"
                    label={t('profile.fields.username')}
                    rules={[
                      { required: true, message: t('profile.validation.usernameRequired') },
                      { min: 3, message: t('profile.validation.usernameMinLength') },
                      { max: 50, message: t('profile.validation.usernameMaxLength') },
                    ]}
                  >
                    <Input prefix={<UserOutlined />} placeholder={t('profile.placeholders.username')} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="email"
                    label={t('profile.fields.email')}
                    rules={[
                      { type: 'email', message: t('profile.validation.emailInvalid') },
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder={t('profile.placeholders.email')} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginTop: '24px' }}>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                    {t('profile.buttons.saveChanges')}
                  </Button>
                  <Button onClick={() => setEditMode(false)} disabled={loading}>
                    {t('profile.buttons.cancel')}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          ) : (
            <Descriptions column={2} bordered>
              <Descriptions.Item label={t('profile.fields.username')}>{user?.username}</Descriptions.Item>
              <Descriptions.Item label={t('profile.fields.email')}>{user?.email || t('profile.fields.notSet')}</Descriptions.Item>
              <Descriptions.Item label={t('profile.fields.userId')}>{user?.id}</Descriptions.Item>
              <Descriptions.Item label={t('profile.fields.role')}>
                <Tag color={getRoleColor(user?.role || '')}>
                  {getRoleDisplayName(user?.role || '', t)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('profile.fields.accountStatus')}>
                <Tag color={user?.is_active ? 'green' : 'orange'}>
                  {user?.is_active ? t('profile.status.active') : t('profile.status.inactive')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('profile.fields.registrationTime')}>
                {user?.created_at ? new Date(user.created_at).toLocaleString() : t('profile.fields.unknown')}
              </Descriptions.Item>
            </Descriptions>
          )}
        </AppCard>
      ),
    },
    {
      key: 'security',
      label: t('profile.tabs.security'),
      children: (
        <AppCard>
          <Title level={4} style={{ marginBottom: '24px' }}>
            <SafetyOutlined /> {t('profile.sections.changePassword')}
          </Title>

          {changePasswordMode ? (
            <Form
              onFinish={handlePasswordChange}
              layout="vertical"
            >
              <Form.Item
                name="currentPassword"
                label={t('profile.fields.currentPassword')}
                rules={[{ required: true, message: t('profile.validation.currentPasswordRequired') }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('profile.placeholders.currentPassword')} />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label={t('profile.fields.newPassword')}
                rules={[{ validator: validatePassword }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('profile.placeholders.newPassword')} />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label={t('profile.fields.confirmPassword')}
                dependencies={['newPassword']}
                rules={[validateConfirmPassword]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('profile.placeholders.confirmPassword')} />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                    {t('profile.buttons.changePassword')}
                  </Button>
                  <Button onClick={() => setChangePasswordMode(false)} disabled={loading}>
                    {t('profile.buttons.cancel')}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          ) : (
            <div>
              <Text>{t('profile.messages.passwordSecurityAdvice')}</Text>
              <div style={{ marginTop: '24px' }}>
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  onClick={() => setChangePasswordMode(true)}
                >
                  {t('profile.buttons.changePassword')}
                </Button>
              </div>
            </div>
          )}
        </AppCard>
      ),
    },
  ]

  // 如果有公司信息，添加公司信息标签页
  if (company) {
    baseItems.push({
      key: 'company',
      label: t('profile.tabs.company'),
      children: (
        <AppCard>
          <Title level={4} style={{ marginBottom: '16px' }}>
            {company.name}
          </Title>
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('profile.fields.companyName')}>{company.name}</Descriptions.Item>
            <Descriptions.Item label={t('profile.fields.companyDescription')}>
              {company.description || t('profile.fields.noDescription')}
            </Descriptions.Item>
            <Descriptions.Item label={t('profile.fields.creationTime')}>
              {company.created_at ? new Date(company.created_at).toLocaleString() : t('profile.fields.unknown')}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary">
              {t('profile.messages.companyInfoNote')}
            </Text>
          </div>
        </AppCard>
      ),
    })
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <UserOutlined /> {t('profile.title')}
        </Title>
        <Text type="secondary">{t('profile.subtitle')}</Text>
      </div>

      <Tabs defaultActiveKey="profile" items={baseItems} />
    </div>
  )
}

export default ProfilePage